import React from 'react';
import { Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useTranslation } from '../i18n';

export type TagFilterOption = { value: string; label: string };

export type TagFilterRowConfig = {
  key: string;
  label: string;
  chipPrefix: string;
  value: string;
  options: TagFilterOption[];
  onChange: (value: string) => void;
  /** 为 false 时已选条件不可删除，清空也不重置该行。默认 true。 */
  clearable?: boolean;
};

export type TagFilterExtraChip = {
  key: string;
  prefix: string;
  label: string;
  onRemove: () => void;
};

type TagFilterPanelProps = {
  rows: TagFilterRowConfig[];
  className?: string;
  extraChips?: TagFilterExtraChip[];
  onClear?: () => void;
  children?: React.ReactNode;
};

const tagActiveClass =
  'rounded-full px-3.5 py-1 text-sm text-white bg-[linear-gradient(rgb(36,175,221),rgb(59,77,215))]';

const tagIdleClass =
  'cursor-pointer border-0 bg-transparent px-1 py-1 text-sm text-slate-600 hover:text-[#596ae8]';

export function TagFilterPanel({ rows, className = '', extraChips = [], onClear, children }: TagFilterPanelProps) {
  const { t } = useTranslation();
  const activeRows = rows.filter((row) => row.value !== 'all');

  const clearAll = () => {
    rows.forEach((row) => {
      if (row.clearable === false) return;
      row.onChange('all');
    });
    onClear?.();
  };

  return (
    <section
      className={`rounded-xl border border-[#eef0f4] bg-white px-5 py-1 shadow-[0_1px_4px_rgba(15,23,42,0.05)] ${className}`}
    >
      <div className="flex flex-wrap items-center gap-3 border-b border-[#eef0f4] py-3.5">
        <span className="shrink-0 text-sm text-slate-500">{t('filter.selected')}</span>
        <div className="flex min-h-[28px] flex-1 flex-wrap items-center gap-2">
          {activeRows.map((row) => {
            const chipLabel =
              row.options.find((opt) => opt.value === row.value)?.label ?? row.value;
            const clearable = row.clearable !== false;
            return (
              <span
                key={row.key}
                className="inline-flex items-center gap-1 rounded-full bg-[#f1f5f9] px-2.5 py-1 text-xs text-slate-600"
              >
                {row.chipPrefix}: {chipLabel}
                {clearable ? (
                  <button
                    type="button"
                    className="cursor-pointer border-0 bg-transparent p-0 text-slate-400 hover:text-slate-600"
                    onClick={() => row.onChange('all')}
                    aria-label={t('filter.remove', { name: row.chipPrefix })}
                  >
                    <CloseOutlined className="text-[10px]" />
                  </button>
                ) : null}
              </span>
            );
          })}
          {extraChips.map((chip) => (
            <span
              key={chip.key}
              className="inline-flex items-center gap-1 rounded-full bg-[#f1f5f9] px-2.5 py-1 text-xs text-slate-600"
            >
              {chip.prefix}: {chip.label}
              <button
                type="button"
                className="cursor-pointer border-0 bg-transparent p-0 text-slate-400 hover:text-slate-600"
                onClick={chip.onRemove}
                aria-label={t('filter.remove', { name: chip.prefix })}
              >
                <CloseOutlined className="text-[10px]" />
              </button>
            </span>
          ))}
        </div>
        <Button
          size="small"
          className="rounded-full border-[#596ae8] text-[#596ae8] text-xs"
          onClick={clearAll}
        >
          {t('filter.clear')}
        </Button>
      </div>

      {rows.map((row, index) => (
        <div
          key={row.key}
          className={`flex gap-4 py-3.5 ${index < rows.length - 1 ? 'border-b border-[#eef0f4]' : ''}`}
        >
          <span className="w-[72px] shrink-0 pt-0.5 text-sm text-slate-500">{row.label}</span>
          <div className="flex flex-1 flex-wrap gap-x-4 gap-y-2">
            {row.options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={row.value === opt.value ? tagActiveClass : tagIdleClass}
                onClick={() => row.onChange(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      ))}
      {children}
    </section>
  );
}
